// WEB-469 (BC-1): top-level React crash boundary. Wraps Sentry.ErrorBoundary
// (reports the crash to Sentry when VITE_SENTRY_DSN is set — a no-op wrapper
// otherwise, since the SDK never initialised) so a render crash shows a
// Russian-language fallback instead of a white screen.
import React from 'react';
import * as Sentry from '@sentry/react';
import { Button, Result } from 'antd';
import { reportClientCrash } from './reportClientCrash';
import { pickStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';

function handleReload(): void {
  window.location.reload();
}

/**
 * WEB-1874: текст экрана падения берётся из словаря НЕ хуком, а `pickStrings`.
 * Это граница аварии: сюда попадают в том числе падения, случившиеся до/вне
 * дерева с провайдером стора, и хук `useStrings` там мог бы упасть вторым
 * разом поверх первого. `pickStrings` читает текущий язык кабинета из
 * модульной переменной и работает в любом состоянии приложения.
 */
function ErrorFallback(): React.ReactElement {
  const s = pickStrings(commonStrings);
  return (
    <Result
      status="error"
      title={s.crashTitle}
      subTitle={s.crashSubtitle}
      extra={
        <Button type="primary" onClick={handleReload}>
          {s.reloadPage}
        </Button>
      }
    />
  );
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

export function AppErrorBoundary({
  children,
}: AppErrorBoundaryProps): React.ReactElement {
  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorFallback />}
      // WEB-464 (#2) / WEB-523: count the FE crash in our own admin-panel
      // counter (feCrashes) AND forward message/stack/url so the admin-panel
      // error screen has detail (Better Stack's Errors API cannot return it
      // over REST). The backend scrubs PII/secrets before persisting
      // (AdminErrorCaptureService.captureError) — never let a report failure
      // throw here.
      onError={(error: unknown) =>
        reportClientCrash({
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          url: window.location.href,
        })
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
